// ==================== Base Model ====================

export type ModelType = 'chat' | 'image' | 'video' | 'audio';

export interface ModelPricing {
  prompt?: string;           // per input token (OpenRouter chat)
  completion?: string;       // per output token (OpenRouter chat)
  per_image?: string;        // per image (OpenRouter image, Runware image)
  per_second?: string;       // per second (Runware video/audio)
  per_generation?: string;   // per generation (Suno)
  per_1k_chars?: string;     // per 1k characters (TTS)
  web_search?: string;       // per web search call (OpenRouter :online)
  request?: string;          // fixed per-request fee
}

export interface ModelCapabilities {
  input_modalities?: string[];
  output_modalities?: string[];
  max_resolution?: number;
  supports_lora?: boolean;
  /** Optional behaviours the model honours, e.g. "tools" (tool calling) or "json_schema". */
  features?: string[];
  /** Context window in tokens, when the registry publishes it. */
  max_context?: number;
}

// ==================== Registry Constraints (new field-driven) ====================

export interface ConstraintField {
  type?: 'number' | 'integer' | 'string' | 'boolean' | 'array' | 'object';
  required?: boolean;
  min?: number;
  max?: number;
  enum?: Array<string | number | boolean>;
  default?: unknown;
  items?: Record<string, unknown>;
  [key: string]: unknown;
}

/** Raw response from GET /models/:id/constraints */
export interface ModelConstraintsRawResponse {
  model_id: string;
  known_model: boolean;
  constraints: {
    image?: { fields: Record<string, ConstraintField>; capabilities?: Record<string, unknown> };
    video?: { fields: Record<string, ConstraintField>; capabilities?: Record<string, unknown> };
    audio?: { fields: Record<string, ConstraintField>; capabilities?: Record<string, unknown> };
  };
  provider_settings?: Record<string, unknown>;
}

// Existing ModelConstraintsResponse stays — but it now represents a single modality:
export interface ModelConstraintsResponse {
  model_id: string;
  fields?: Record<string, ConstraintField>;
  capabilities?: Record<string, unknown>;
  defaults?: Record<string, unknown>;
  /** Provider-specific enum settings, e.g. { openai: { quality: ["low","medium","high"] } }. */
  provider_settings?: Record<string, unknown>;
  [key: string]: unknown;
}

// ==================== Provider Extensions ====================

export interface OpenRouterModelExt {
  context_length?: number;
  modality?: string;
}

export interface RunwareModelExt {
  air?: string;
  architecture?: string;
  category?: string;
  version?: string;
  civitai_id?: string;
  nsfw_level?: number;
  tags?: string[];
}

/**
 * Per-model block a generator-profile catalog entry (provider "profile") carries
 * — see contentgen-model-registry/app/data/profile_models.py `_map_profile`.
 * Every field is optional: the registry is the source of truth and older
 * deployments predate the input-compatibility keys, so consumers must degrade
 * rather than assume.
 */
export interface ProfileModelExt {
  slug?: string;
  version?: number;
  adapters?: number;
  controls?: number;
  control_types?: string[];
  user_id?: string | null;
  /** Inference mode the profile runs in: 'txt2img' | 'img2img' | 'inpaint'. */
  generation_mode?: string;
  /** True for img2img and inpaint — the run must supply a seed image attachment. */
  requires_seed_image?: boolean;
  /** True for inpaint — the run must additionally supply an attachment with role "mask". */
  requires_mask?: boolean;
}

// ==================== Unified Model ====================

export interface AIModel {
  id: string;
  name: string;
  description?: string;
  provider: string;
  type: ModelType;
  pricing?: ModelPricing;
  capabilities?: ModelCapabilities;
  openrouter?: OpenRouterModelExt;
  runware?: RunwareModelExt;
  /** Only on entries with provider "profile". */
  profile?: ProfileModelExt;
  [key: string]: unknown;
}

// ==================== API Response ====================

export interface ModelsResponse {
  recommended: AIModel[];
  openrouter?: AIModel[];
  runware?: AIModel[];
  suno?: AIModel[];
  vertexai?: AIModel[];
  /** Published generator profiles served as image models (provider "profile"). */
  profile?: AIModel[];
  /** Modal Endpoints chat models (provider "modal_endpoints"). */
  modal_endpoints?: AIModel[];
  /** Anthropic's own chat models (provider "anthropic"): for pipeline agent,
   *  prompt, distributor and sequencer steps and the chat, not the Builder
   *  chat or the reviewer judges. Only on a server that has an Anthropic key
   *  (absent otherwise). */
  anthropic?: AIModel[];
  [key: string]: AIModel[] | undefined;
}

// ==================== Helpers ====================

export function supportsLoRA(model: AIModel): boolean {
  return model.capabilities?.supports_lora === true;
}

export function getArchitecture(model: AIModel): string | undefined {
  return model.runware?.architecture;
}

export function isImageModel(model: AIModel): boolean {
  return model.type === 'image';
}

export function isVideoModel(model: AIModel): boolean {
  return model.type === 'video';
}

export function isChatModel(model: AIModel): boolean {
  return model.type === 'chat';
}

export function formatPrice(model: AIModel): string {
  const p = model.pricing;
  if (!p) return '';

  if (model.type === 'chat') {
    if (p.prompt || p.completion) {
      const promptPrice = parseFloat(p.prompt ?? '0');
      const completionPrice = parseFloat(p.completion ?? '0');
      const blendedPer1k = (promptPrice > 0 ? promptPrice * 1000 : 0) + (completionPrice > 0 ? completionPrice * 1000 : 0);
      if (blendedPer1k > 0) return `$${blendedPer1k.toFixed(4)}/1k in+out tok`;
    }
  }

  if (p.per_image) {
    const price = parseFloat(p.per_image);
    if (price > 0) return `$${price.toFixed(4)}/img`;
  }

  if (p.per_second) {
    const price = parseFloat(p.per_second);
    if (price > 0) return `$${price.toFixed(4)}/sec`;
  }

  if (p.per_generation) {
    const price = parseFloat(p.per_generation);
    if (price > 0) return `$${price.toFixed(2)}/gen`;
  }

  if (p.per_1k_chars) {
    const price = parseFloat(p.per_1k_chars);
    if (price > 0) return `$${price.toFixed(4)}/1k chars`;
  }

  if (p.request) {
    const price = parseFloat(p.request);
    if (price > 0) return `$${price.toFixed(4)}/request`;
  }

  if (p.web_search) {
    const price = parseFloat(p.web_search);
    if (price > 0) return `$${price.toFixed(4)}/search`;
  }

  if (p.prompt || p.completion) {
    const promptPrice = parseFloat(p.prompt ?? '0');
    const completionPrice = parseFloat(p.completion ?? '0');
    const blendedPer1k = (promptPrice > 0 ? promptPrice * 1000 : 0) + (completionPrice > 0 ? completionPrice * 1000 : 0);
    if (blendedPer1k > 0) return `$${blendedPer1k.toFixed(4)}/1k in+out tok`;
  }

  return '';
}

const DEFAULT_MARKUP = parseFloat(process.env.REACT_APP_MARKUP || '1.7');
const DEFAULT_CREDITS_PER_USD = parseFloat(process.env.REACT_APP_CREDITS_PER_USD || '100');

export const usdToCredits = (usd: number): number => {
  if (!Number.isFinite(usd) || usd <= 0) return 0;
  return Math.ceil(usd * DEFAULT_MARKUP * DEFAULT_CREDITS_PER_USD);
};

export function formatCreditPrice(model: AIModel): string {
  const p = model.pricing;
  if (!p) return '';

  if (model.type === 'chat') {
    if (p.prompt || p.completion) {
      const promptPrice = parseFloat(p.prompt ?? '0');
      const completionPrice = parseFloat(p.completion ?? '0');
      const blendedPer1k = (promptPrice > 0 ? promptPrice * 1000 : 0) + (completionPrice > 0 ? completionPrice * 1000 : 0);
      if (blendedPer1k > 0) return `${usdToCredits(blendedPer1k)} cr/1k in+out tok`;
    }
  }

  if (p.per_image) {
    const price = parseFloat(p.per_image);
    if (price > 0) return `${usdToCredits(price)} cr/img`;
  }

  if (p.per_second) {
    const price = parseFloat(p.per_second);
    if (price > 0) return `${usdToCredits(price)} cr/sec`;
  }

  if (p.per_generation) {
    const price = parseFloat(p.per_generation);
    if (price > 0) return `${usdToCredits(price)} cr/gen`;
  }

  if (p.per_1k_chars) {
    const price = parseFloat(p.per_1k_chars);
    if (price > 0) return `${usdToCredits(price)} cr/1k chars`;
  }

  if (p.request) {
    const price = parseFloat(p.request);
    if (price > 0) return `${usdToCredits(price)} cr/request`;
  }

  if (p.web_search) {
    const price = parseFloat(p.web_search);
    if (price > 0) return `${usdToCredits(price)} cr/search`;
  }

  if (p.prompt || p.completion) {
    const promptPrice = parseFloat(p.prompt ?? '0');
    const completionPrice = parseFloat(p.completion ?? '0');
    const blendedPer1k = (promptPrice > 0 ? promptPrice * 1000 : 0) + (completionPrice > 0 ? completionPrice * 1000 : 0);
    if (blendedPer1k > 0) return `${usdToCredits(blendedPer1k)} cr/1k in+out tok`;
  }

  return '';
}
